/**
 * The `link` mark's one interaction, in one place — the toolbar button and `Mod+K` are the same
 * gesture and must not drift apart.
 *
 * `120-non-functional.md` §1.1: *"`link` carries an `href`, and `href` is a string.
 * `javascript:alert(1)` is a perfectly well-formed string."* The Link extension is configured
 * with `protocols: ['http', 'https', 'mailto']` (`features/editor/extensions.ts`), which is the
 * enforcement; this function does not re-implement it and must not be mistaken for it. It is a
 * prompt.
 */
import type { Editor } from '@tiptap/react';
import { translate } from '@/lib/i18n';

/** Prompt for a URL and apply (or, on an empty string, remove) the link over the selection. */
export function promptForLink(editor: Editor): void {
  const previous: unknown = editor.getAttributes('link')['href'];
  const href = window.prompt(
    translate('editor.link.prompt'),
    typeof previous === 'string' ? previous : 'https://',
  );
  if (href === null) return; // cancelled

  const chain = editor.chain().focus().extendMarkRange('link');

  if (href.trim() === '') {
    chain.unsetLink().run();
    return;
  }
  chain.setLink({ href }).run();
}

/**
 * Is the caret inside the document with a NON-EMPTY selection?
 *
 * `110-design-system.md` §7 makes `Mod+K` the app's one overloaded binding: *"Insert link when
 * there is a text selection; command palette when there is not. The only overloaded binding, and
 * it is the convention everywhere."*
 *
 * Both claimants listen on different layers — Tiptap on the editable element, the palette on
 * `window` — and a `preventDefault()` in one does **not** stop the other from running. So the
 * overload cannot be resolved by ordering; it has to be resolved by *both sides asking the same
 * question*. Tiptap's `Mod-k` returns `false` (declining the key) when the selection is empty,
 * and the palette declines it, via this function, when it is not.
 *
 * Read from the DOM rather than from an `Editor` instance because the palette is mounted in the
 * shell and has no handle on the editor — and because "the user has selected text in the
 * document" is a fact about the document, not about React.
 */
export function editorHasTextSelection(): boolean {
  if (typeof document === 'undefined') return false;

  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !active.isContentEditable) return false;

  const selection = window.getSelection();
  return selection !== null && !selection.isCollapsed && selection.toString().length > 0;
}
